import type { Edge, Node } from '../../../core/models/scene';
import { GenogramPerspectiveProjection } from '../services/GenogramPerspectiveProjection';

type CulturePalette = Record<string, string>;
type HealthPalette = Record<string, { color: string; icon?: string; propagate?: boolean }>;

const TOP_LIMIT = 8;

const humanizeTagKey = (tag: string): string =>
  tag
    .split('_')
    .map((part) => (part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
    .join(' ');

export const normalizeTag = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '_');

export const tagsMatch = (left: string, right: string): boolean => {
  const a = normalizeTag(left);
  const b = normalizeTag(right);
  if (a === b) return true;
  return a.replace(/_/g, ' ') === b.replace(/_/g, ' ');
};

export interface LegendCultureItem {
  tag: string;
  label: string;
  color: string;
  count: number;
}

export interface LegendHealthItem {
  tag: string;
  label: string;
  color: string;
  count: number;
  icon?: string;
}

export interface LegendHealthFocusStats {
  conditionTag: string;
  conditionLabel: string;
  color: string;
  icon: string;
  direct: number;
  inheritedRisk: number;
  indexName: string | null;
}

interface BuildLegendDataOptions {
  nodes: Node[];
  edges: Edge[];
  culturePalette: CulturePalette;
  healthPalette: HealthPalette;
  healthFocusCondition?: string | null;
  healthFocusIndexPersonId?: string | null;
  cultureTagLabel?: (tag: string) => string;
  healthTagLabel?: (tag: string) => string;
}

const readStringArray = (...values: unknown[]): string[] => {
  for (const value of values) {
    if (!Array.isArray(value)) continue;
    const next = value
      .map((entry) => String(entry ?? '').trim())
      .filter((entry) => entry.length > 0);
    if (next.length > 0) return next;
  }
  return [];
};

export function buildGenogramViewpointLegendData(opts: BuildLegendDataOptions): {
  culturalItems: LegendCultureItem[];
  healthItems: LegendHealthItem[];
  healthFocusStats: LegendHealthFocusStats | null;
} {
  const cultureTagLabel = opts.cultureTagLabel ?? ((tag: string) => humanizeTagKey(normalizeTag(tag)));
  const healthTagLabel = opts.healthTagLabel ?? ((tag: string) => humanizeTagKey(normalizeTag(tag)));

  const projection = GenogramPerspectiveProjection.compute(opts.nodes, opts.edges, {
    culturePalette: opts.culturePalette,
    healthPalette: opts.healthPalette,
  });

  const cultureCounts = new Map<string, { count: number; color: string }>();
  const healthCounts = new Map<string, { count: number; color: string; icon?: string }>();

  let direct = 0;
  let inheritedRisk = 0;
  const focusCondition = opts.healthFocusCondition ? normalizeTag(opts.healthFocusCondition) : null;

  for (const node of opts.nodes) {
    const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
    const props = ((node as any).props ?? {}) as Record<string, unknown>;
    const explicitHealth = readStringArray(attrs.medicalConditionTags, props.medicalConditionTags, (node as any).medicalConditionTags);

    const cultureMix = projection.culture.get(node.id) ?? [];
    cultureMix.forEach((entry) => {
      if (entry.weight <= 0) return;
      const existing = cultureCounts.get(entry.tag);
      if (!existing) {
        cultureCounts.set(entry.tag, { count: 1, color: entry.color });
      } else {
        existing.count += 1;
      }
    });

    const healthMix = projection.health.get(node.id) ?? [];
    healthMix.forEach((entry) => {
      const existing = healthCounts.get(entry.tag);
      if (!existing) {
        healthCounts.set(entry.tag, { count: 1, color: entry.color, icon: entry.icon });
      } else {
        existing.count += 1;
      }
    });

    if (!focusCondition) continue;
    const computed = healthMix.map((entry) => entry.tag).filter((entry) => tagsMatch(entry, focusCondition));
    if (computed.length === 0) continue;
    if (explicitHealth.some((entry) => tagsMatch(entry, focusCondition))) {
      direct += 1;
    } else {
      inheritedRisk += 1;
    }
  }

  const culturalItems: LegendCultureItem[] = Array.from(cultureCounts.entries())
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, TOP_LIMIT)
    .map(([tag, value]) => ({
      tag,
      label: cultureTagLabel(tag),
      color: value.color,
      count: value.count,
    }));

  const healthItems: LegendHealthItem[] = Array.from(healthCounts.entries())
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, TOP_LIMIT)
    .map(([tag, value]) => ({
      tag,
      label: healthTagLabel(tag),
      color: value.color,
      count: value.count,
      icon: value.icon,
    }));

  const healthFocusStats = (() => {
    if (!focusCondition) return null;
    const palette = opts.healthPalette[focusCondition];
    const indexNameRaw = opts.healthFocusIndexPersonId
      ? opts.nodes.find((n) => n.id === opts.healthFocusIndexPersonId)?.name
      : null;
    return {
      conditionTag: focusCondition,
      conditionLabel: healthTagLabel(focusCondition),
      icon: palette?.icon ?? '•',
      color: palette?.color ?? '#6366f1',
      direct,
      inheritedRisk,
      indexName: typeof indexNameRaw === 'string' ? indexNameRaw : null,
    };
  })();

  return { culturalItems, healthItems, healthFocusStats };
}
